import React, { useState, useRef } from 'react'
import {
  Box,
  Chip,
  IconButton,
  useTheme,
  SxProps,
  lighten
} from '@mui/material'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { APP_COLOR } from 'utils/colors'

export interface ChipData {
  title: string
  key: string
}

interface HorizontalChipsBarProps {
  chips: ChipData[]
  onChipSelect: (selectedChip: ChipData) => void
  leftButtonStyle?: SxProps
  rightButtonStyle?: SxProps
  selectedKey?: string
}

const HorizontalChipsBar: React.FC<HorizontalChipsBarProps> = ({
  chips,
  onChipSelect,
  leftButtonStyle,
  rightButtonStyle,
  selectedKey
}) => {
  const [selectedChip, setSelectedChip] = useState<ChipData | null>(
    chips.find((chip) => chip.key === selectedKey) || null
  )
  const [showButtons, setShowButtons] = useState(false)
  const theme = useTheme()
  const boxRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollLeftRef = useRef(0)

  const handleChipClick = (chip: ChipData) => {
    setSelectedChip(chip)
    onChipSelect(chip)
  }

  const scrollLeft = () => {
    if (boxRef.current) {
      boxRef.current.scrollBy({ left: -100, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (boxRef.current) {
      boxRef.current.scrollBy({ left: 100, behavior: 'smooth' })
    }
  }

  const commonButtonStyle: SxProps = {
    opacity: showButtons ? 1 : 0,
    transition: 'opacity 0.3s',
    zIndex: 1,
    backgroundColor: 'white',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px', // Ensure consistent width
    height: '29px', // Ensure consistent height
    '&:hover': {
      backgroundColor: 'white'
    }
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    isDragging.current = true
    startX.current = event.pageX - (boxRef.current?.offsetLeft || 0)
    scrollLeftRef.current = boxRef.current?.scrollLeft || 0
  }

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    event.preventDefault()
    const x = event.pageX - (boxRef.current?.offsetLeft || 0)
    const walk = (x - startX.current) * 2 // Adjust the scroll speed
    if (boxRef.current) {
      boxRef.current.scrollLeft = scrollLeftRef.current - walk
    }
  }

  const handleMouseUp = () => {
    isDragging.current = false
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(2, 0),
        marginRight: '-20px',
        marginLeft: '-20px',
        '&:hover .scroll-button': {
          opacity: 1
        }
      }}
      onMouseEnter={() => setShowButtons(true)}
      onMouseLeave={() => setShowButtons(false)}
    >
      <IconButton
        className="scroll-button"
        onClick={scrollLeft}
        sx={{
          position: 'absolute',
          left: 8,
          ...commonButtonStyle,
          ...leftButtonStyle
        }}
      >
        <ArrowBackIosIcon
          sx={{ marginLeft: 2, width: '20px', height: '20px' }}
        />
      </IconButton>
      <Box
        ref={boxRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          padding: theme.spacing(2, 0),
          '& > *': {
            marginRight: theme.spacing(2)
          },
          '& > :first-of-type': {
            marginLeft: theme.spacing(2)
          },
          '& > :last-of-type': {
            marginRight: theme.spacing(2)
          },
          // Hide scrollbar for Chrome, Safari, and Opera
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          // Hide scrollbar for IE, Edge, and Firefox
          '-ms-overflow-style': 'none', // IE and Edge
          'scrollbar-width': 'none' // Firefox
        }}
      >
        {chips.map((chip) => (
          <Chip
            key={chip.key}
            label={chip.title}
            clickable
            onClick={() => handleChipClick(chip)}
            sx={{
              height: '29px !important', // Ensure consistent height
              marginRight: theme.spacing(2),
              fontSize: '14px',
              color: selectedChip?.key === chip.key ? '#934544' : '#707173',
              backgroundColor:
                selectedChip?.key === chip.key
                  ? lighten(APP_COLOR, 0.8)
                  : 'default',
              '&:hover': {
                backgroundColor:
                  selectedChip?.key === chip.key
                    ? lighten(APP_COLOR, 0.8)
                    : 'hoverColor' // Replace 'hoverColor' with your desired hover color for non-selected chips
              }
            }}
          />
        ))}
      </Box>
      <IconButton
        className="scroll-button"
        onClick={scrollRight}
        sx={{
          position: 'absolute',
          right: 8,
          ...commonButtonStyle,
          ...rightButtonStyle
        }}
      >
        <ArrowForwardIosIcon sx={{ width: '20px', height: '20px' }} />
      </IconButton>
    </Box>
  )
}

export default HorizontalChipsBar
